import { memo } from 'react';
import {
  Bell, BellOff, ChevronsDown, ChevronsUp, Download, ExternalLink, FileText,
  History, Link, Link2, Lock, Mail, MailMinus, MailPlus, MoreVertical, Pencil,
  Pin, PinOff, Sparkles, Star, Trash2, Users, Users2,
} from 'lucide-react';
import { Badge } from '../../ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import SavedViewSparkline from '../SavedViewSparkline';

const SavedViewRow = memo(({
  view: v,
  isTop,
  onApply,
  onOpenInNewTab,
  onCopyLink,
  onToggleSubscribe,
  onToggleSubscriptionEmail,
  onOpenSchedule,
  onSetDefault,
  onClearDefault,
  onSetTeamDefault,
  onEdit,
  onDelete,
  onExportCsv,
  onExportPdf,
  onTogglePin,
  onToggleFavourite,
  onMoveOrder,
  onSetSlug,
  onShowActivity,
  onShowAnalytics,
  canSetTeamDefault,
}) => (
  <div
    className="group flex items-center gap-1 px-3 py-1.5 hover:bg-muted/60 transition"
    data-testid={`saved-view-item-${v.id}`}
  >
    <button
      type="button"
      onClick={() => onApply(v)}
      className="flex-1 min-w-0 text-left"
      data-testid={`saved-view-apply-${v.id}`}
    >
      <div className="flex items-center gap-1.5 flex-wrap">
        {v.shared
          ? <Users className="h-3 w-3 text-green-600 flex-shrink-0" title="Shared with team" />
          : <Lock className="h-3 w-3 text-muted-foreground flex-shrink-0" title="Private" />}
        <span className="text-sm font-medium truncate">{v.name}</span>
        {v.is_default_for_path && (
          <Pin
            className="h-3 w-3 text-[#ff6600]"
            title="Default view for this page"
            data-testid={`saved-view-default-${v.id}`}
          />
        )}
        {v.is_pinned && (
          <Pin
            className="h-3 w-3 text-[#111565] fill-[#111565]/20"
            title="Pinned to your Dashboard"
            data-testid={`saved-view-pinned-icon-${v.id}`}
          />
        )}
        {v.is_subscribed && (
          <Bell className="h-3 w-3 text-blue-500" title="Subscribed — you'll be notified on matches" />
        )}
        {v.schedule?.enabled && (
          <Mail className="h-3 w-3 text-[#111565]" title={`Emailed ${v.schedule.frequency}`} />
        )}
        {isTop && (
          <span
            className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full bg-emerald-100 text-emerald-700 text-[9px] font-semibold uppercase tracking-wide"
            title={`Used ${v.access_count} ${v.access_count === 1 ? 'time' : 'times'}`}
            data-testid={`saved-view-recently-used-${v.id}`}
          >
            <Sparkles className="h-2.5 w-2.5" /> Top
          </span>
        )}
      </div>
      {(v.tags || []).length > 0 && (
        <div className="flex flex-wrap gap-1 mt-0.5 pl-4">
          {v.tags.slice(0, 4).map((t) => (
            <Badge key={`${v.id}-${t}`} variant="secondary" className="h-4 px-1 text-[9px]">
              {t}
            </Badge>
          ))}
        </div>
      )}
      {v.query_string && (
        <p className="text-[10px] text-muted-foreground truncate pl-4">
          {decodeURIComponent(v.query_string)}
        </p>
      )}
      <div className="pl-4 mt-0.5 group-hover:block hidden">
        <SavedViewSparkline viewId={v.id} eager onClick={() => onShowAnalytics(v)} />
      </div>
    </button>

    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onToggleFavourite(v); }}
      className={`p-1 rounded hover:bg-background transition ${v.is_favourited ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
      aria-label={v.is_favourited ? 'Unfavourite' : 'Favourite'}
      title={v.is_favourited ? 'Unfavourite' : 'Favourite'}
      data-testid={`saved-view-favourite-${v.id}`}
    >
      <Star className={`h-3.5 w-3.5 ${v.is_favourited ? 'text-amber-400 fill-amber-400' : 'text-muted-foreground'}`} />
    </button>

    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onOpenInNewTab(v); }}
      className="p-1 rounded hover:bg-background opacity-0 group-hover:opacity-100 transition"
      aria-label="Open in new tab"
      title="Open in new tab"
      data-testid={`saved-view-open-new-tab-${v.id}`}
    >
      <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
    </button>
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onCopyLink(v); }}
      className="p-1 rounded hover:bg-background opacity-0 group-hover:opacity-100 transition"
      aria-label="Copy link"
      title="Copy link"
      data-testid={`saved-view-copy-${v.id}`}
    >
      <Link2 className="h-3.5 w-3.5 text-muted-foreground" />
    </button>

    <DropdownMenu>
      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="p-1 rounded hover:bg-background opacity-60 group-hover:opacity-100 transition"
          aria-label="More actions"
          data-testid={`saved-view-more-${v.id}`}
        >
          <MoreVertical className="h-3.5 w-3.5 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuItem
          onClick={() => onToggleSubscribe(v)}
          data-testid={`saved-view-subscribe-${v.id}`}
        >
          {v.is_subscribed
            ? <><BellOff className="h-3.5 w-3.5 mr-2" /> Unsubscribe</>
            : <><Bell className="h-3.5 w-3.5 mr-2" /> Subscribe to matches</>}
        </DropdownMenuItem>
        {v.is_subscribed && (
          <DropdownMenuItem
            onClick={() => onToggleSubscriptionEmail(v)}
            data-testid={`saved-view-subemail-${v.id}`}
          >
            {v.subscription_email_optin
              ? <><MailMinus className="h-3.5 w-3.5 mr-2" /> Stop email digest</>
              : <><MailPlus className="h-3.5 w-3.5 mr-2" /> Email me on matches</>}
          </DropdownMenuItem>
        )}
        {v.is_owner && (
          <DropdownMenuItem
            onClick={() => onOpenSchedule(v)}
            data-testid={`saved-view-schedule-${v.id}`}
          >
            <Mail className="h-3.5 w-3.5 mr-2" />
            {v.schedule?.enabled ? `Email · ${v.schedule.frequency}` : 'Email schedule…'}
          </DropdownMenuItem>
        )}
        {v.is_default_for_path
          ? (
            <DropdownMenuItem
              onClick={() => onClearDefault(v)}
              data-testid={`saved-view-cleardefault-${v.id}`}
            >
              <Pin className="h-3.5 w-3.5 mr-2" /> Clear default
            </DropdownMenuItem>
          )
          : (
            <DropdownMenuItem
              onClick={() => onSetDefault(v)}
              data-testid={`saved-view-setdefault-${v.id}`}
            >
              <Pin className="h-3.5 w-3.5 mr-2" /> Set as default for this page
            </DropdownMenuItem>
          )}
        {canSetTeamDefault && v.shared && (
          <DropdownMenuItem
            onClick={() => onSetTeamDefault(v)}
            data-testid={`saved-view-teamdefault-${v.id}`}
          >
            <Users2 className="h-3.5 w-3.5 mr-2" /> Set as team default
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onClick={() => onTogglePin(v)}
          data-testid={`saved-view-pin-${v.id}`}
        >
          {v.is_pinned
            ? <><PinOff className="h-3.5 w-3.5 mr-2" /> Unpin from Dashboard</>
            : <><Pin className="h-3.5 w-3.5 mr-2" /> Pin to Dashboard</>}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => onExportCsv(v)}
          data-testid={`saved-view-export-${v.id}`}
        >
          <Download className="h-3.5 w-3.5 mr-2" /> Export matches as CSV
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => onExportPdf(v)}
          data-testid={`saved-view-export-pdf-${v.id}`}
        >
          <FileText className="h-3.5 w-3.5 mr-2" /> Export matches as PDF
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => onShowActivity(v)}
          data-testid={`saved-view-activity-${v.id}`}
        >
          <History className="h-3.5 w-3.5 mr-2" /> Activity log
        </DropdownMenuItem>
        {v.is_owner && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => onMoveOrder(v, -10)}
              data-testid={`saved-view-moveup-${v.id}`}
            >
              <ChevronsUp className="h-3.5 w-3.5 mr-2" /> Move up
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onMoveOrder(v, 10)}
              data-testid={`saved-view-movedown-${v.id}`}
            >
              <ChevronsDown className="h-3.5 w-3.5 mr-2" /> Move down
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onSetSlug(v)}
              data-testid={`saved-view-slug-${v.id}`}
            >
              <Link className="h-3.5 w-3.5 mr-2" />
              {v.slug ? `Permalink · /v/${v.slug}` : 'Create permalink…'}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onEdit(v)} data-testid={`saved-view-edit-${v.id}`}>
              <Pencil className="h-3.5 w-3.5 mr-2" /> Edit name / tags / folder
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onDelete(v)}
              className="text-red-600"
              data-testid={`saved-view-delete-${v.id}`}
            >
              <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete view
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  </div>
));

SavedViewRow.displayName = 'SavedViewRow';

export default SavedViewRow;
